import {
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  input,
  OnInit,
  output,
  signal,
  ViewChild,
} from "@angular/core";
import { DomSanitizer, SafeHtml } from "@angular/platform-browser";
import {
  _AbcFormComponent,
  _AbcScrollViewComponent,
  _AbcSelectBoxComponent,
  _AbcTextAreaComponent,
} from "@barthauer/abc/ui-components";
import {
  DxFormModule,
  DxStepperComponent,
  DxValidatorComponent,
  DxValidatorModule,
} from "devextreme-angular";
import { TruncatePipe } from "../../../../../shared/pipes/truncate-pipe";
import { ButtonOptions } from "@devexpress/analytics-core/analytics-internal";
import { CommonModule } from "@angular/common";
import { DataService } from "../../../../../services/data.service";
import { ApprovalRequestService } from "../approval-request.service";
import { RequestPopUpService } from "./request-popup.service";
import { PermissionContext } from "../../../../../models/enumeration-list.model";
import { ActionIcon, RequestStatus, StepDecision } from "../enums";
import { CheckPermissionDirective } from "../../../../../directives/check-permession-directive";
import { first } from "rxjs";
import { ApprovalStepViewModel } from "../../../../../view-models/approvalStep.view-model";
import { ValidationCallbackData } from "devextreme/common";
import { DataDefinitionOptions } from "../../../../../models/home.model";
import { UbtLoaderComponent } from "../../../../../shared/ubt-loader/ubt-loader.component";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { NotificationService1 } from "../../../../../services/notification1.service";

@Component({
  selector: "ubt-geods-request-popup",
  templateUrl: "./request-popup.component.html",
  styleUrls: ["./request-popup.component.scss"],
  imports: [
    _AbcFormComponent,
    DxFormModule,
    _AbcTextAreaComponent,
    DxStepperComponent,
    TruncatePipe,
    _AbcScrollViewComponent,
    CommonModule,
    _AbcSelectBoxComponent,
    DxValidatorModule,
    CheckPermissionDirective,
    UbtLoaderComponent,
  ],
  providers: [RequestPopUpService],
})
export class RequestPopUp implements OnInit {
  protected isTimeline = input<boolean>();
  protected maturityLevel = input<string>();
  protected readonly timelineRequested = output<boolean>();
  protected readonly newStepAdded = output<void>();
  protected readonly maturityLevelChanged = output<string>();
  // Used In Parent Component to reset scroll index on hiding
  @ViewChild("timelineScrollView")
  public timelineScrollView!: _AbcScrollViewComponent;
  @ViewChild("maturityLevelField")
  public maturityLevelField!: _AbcSelectBoxComponent;
  @ViewChild("maturityLevelValidator")
  public maturityLevelValidator!: DxValidatorComponent;
  @ViewChild("reasonValidator") public reasonValidator!: DxValidatorComponent;

  private readonly requestPopupService = inject(RequestPopUpService);
  private readonly approvalRequestService = inject(ApprovalRequestService);
  private readonly sanitizer = inject(DomSanitizer);
  private readonly dataService: DataService = inject(DataService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly notificationService = inject(NotificationService1);

  protected res: (key: string, ...value: string[]) => string =
    this.dataService.res;
  protected nextMaturityLevel = computed(() =>
    this.approvalRequestService.nextMaturityLevel(this.maturityLevel())
  );
  protected submitIconSvg!: SafeHtml;
  protected expandedReasonIndex: number | null = null;
  protected readonly maxDisplayedChar = 100;
  protected userRole: string;
  protected permission = "";
  protected steps: ApprovalStepViewModel[] = [];
  protected formData = {
    reason: "",
    maturityLevel: "",
  };
  protected isActiveRequest = signal(false);
  protected canWithdraw = signal(false);
  protected permissions = this.approvalRequestService.permissions;
  protected submitButtonOptions: ButtonOptions = {
    text: "",
    useSubmitBehavior: true,
  };
  protected cancelButtonOptions: ButtonOptions = {
    text: this.dataService.res("Ubt-MeasureDetail-Approval-Request-Clear"),
    onClick: () => {
      this.clearForm();
    },
  };
  protected withdrawButtonOptions: ButtonOptions = {
    text: "",
    onClick: () => {
      this.withdrawRequest();
    },
  };
  protected maturityLevelList: DataDefinitionOptions[] = [
    { name: "HG1", value: "HG1" },
    { name: "HG2", value: "HG2" },
    { name: "HG3", value: "HG3" },
    { name: "HG4", value: "HG4" },
    { name: "HG5", value: "HG5" },
  ];
  protected isLoading = signal(false);

  private skipValidation = false;
  private fullName = "";

  constructor() {
    effect(() => {
      this.formData.maturityLevel = this.maturityLevel();
      this.clearForm();
    });
  }

  ngOnInit(): void {
    this.initializeUserRoleAndPermission();
    this.getSteps();
    this.initializeButtonsCongirations();
    this.getUserFullName();
  }

  public clearForm(newMaturityLevel?: string): void {
    this.clearValidation();
    this.skipValidation = true;
    if (
      this.permission === PermissionContext.MeasureChangeHartegrad &&
      this.maturityLevelField
    )
      this.maturityLevelField.value = newMaturityLevel ?? this.maturityLevel();
  }

  protected expandReason(i: number): void {
    this.expandedReasonIndex = this.expandedReasonIndex === i ? null : i;
  }

  protected formatAction(
    userName: string,
    role: string,
    action: string
  ): string {
    return this.requestPopupService.formatAction(userName, role, action);
  }

  protected validateMaturityChange = (
    options: ValidationCallbackData
  ): boolean => {
    if (this.skipValidation) {
      this.skipValidation = false;
      return true;
    }
    return options.value !== this.maturityLevel() || options.value === null;
  };

  protected format(): string {
    return this.approvalRequestService.format(this.steps[0]?.Status);
  }
  protected getStatus(status: string): string {
    return this.requestPopupService.getStatus(status);
  }
  private initializeUserRoleAndPermission(): void {
    this.userRole = this.dataService.getRole()[0];
    this.permission =
      this.approvalRequestService.getApprovalProcessPermission();
  }

  private getSteps(): void {
    this.approvalRequestService.steps$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((steps) => {
        this.steps = steps;
        this.isActiveRequest.set(this.verifyRecentRequest());
        this.canWithdraw.set(this.verifyWithdrawStep());
      });
  }

  private initializeButtonsCongirations(): void {
    this.submitIconSvg = this.sanitizer.bypassSecurityTrustHtml(
      ActionIcon.SUBMIT_REQUEST
    );
    this.submitButtonOptions.text =
      this.requestPopupService.setSubmitButtonText(this.permission);

    this.withdrawButtonOptions.text =
      this.requestPopupService.setWithdrawButtonText(this.permission);
      
  }
  private getUserFullName(): void {
    this.requestPopupService
      .getFullName()
      .pipe(first())
      .subscribe((person) => {
        this.fullName = person[0]?.fullName ?? "";
      });
  }
  public submitAction(e: SubmitEvent): void {
    e.preventDefault();
    switch (this.permission) {
      case PermissionContext.MeasureCreateApprovalRequest:
        this.createRequest();
        break;
      case PermissionContext.MeasureApprovalRequestStep1:
      case PermissionContext.MeasureApprovalRequestStep2:
        this.approveRequest();
        break;
      case PermissionContext.MeasureChangeHartegrad:
        this.adminChangeMaturityLevel();
        break;
    }
  }

  private createRequest(): void {
    if (this.validate()) {
      this.isLoading.set(true);
      this.requestPopupService
        .addNewStep(
          this.steps,
          RequestStatus.IN_PROGRESS,
          this.formData.reason,
          this.fullName,
          this.userRole,
          this.permission,
          this.maturityLevel()
        )
        .subscribe(({ isActiveRequest, canWithdraw }) => {
          this.isActiveRequest.set(isActiveRequest);
          this.canWithdraw.set(canWithdraw);
          this.newStepAdded.emit();
          this.clearForm();
          this.isLoading.set(false);
        });
    }
  }
  private adminChangeMaturityLevel(): void {
    //Retrigger Validation for maturityLevel in case popup closed without any changes.
    const aux = this.formData.maturityLevel;
    this.maturityLevelField.instance.option("value", "ValidationTrigger");
    this.maturityLevelField.instance.option("value", aux);
    if (this.validate() && aux != this.maturityLevel()) {
      this.isLoading.set(true);
      this.requestPopupService
        .addNewStep(
          this.steps,
          RequestStatus.APPROVED,
          this.formData.reason,
          this.fullName,
          this.userRole,
          this.permission,
          this.maturityLevel(),
          ActionIcon.EDIT,
          aux
        )
        .subscribe(({ isActiveRequest, canWithdraw }) => {
          this.isActiveRequest.set(isActiveRequest);
          this.canWithdraw.set(canWithdraw);
          this.newStepAdded.emit();
          this.maturityLevelChanged.emit(aux);
          this.clearForm(aux);
          this.isLoading.set(false);
        });
    }
  }
  private withdrawRequest(): void {
    if (this.validate()) {
      this.isLoading.set(true);
      this.requestPopupService
        .addNewStep(
          this.steps,
          StepDecision.REJECTED,
          this.formData.reason,
          this.fullName,
          this.userRole,
          this.permission,
          this.maturityLevel(),
          ActionIcon.WITHDRAW
        )
        .subscribe(({ isActiveRequest, canWithdraw }) => {
          this.isActiveRequest.set(isActiveRequest);
          this.canWithdraw.set(canWithdraw);
          this.newStepAdded.emit();
          this.clearForm();
          if (
            this.permission === PermissionContext.MeasureApprovalRequestStep1 ||
            this.permission === PermissionContext.MeasureApprovalRequestStep2
          )
            this.timelineRequested.emit(true);
          this.isLoading.set(false);
        });
    }
  }
  private approveRequest(): void {
    if (this.validate()) {
      this.isLoading.set(true);
      this.requestPopupService
        .addNewStep(
          this.steps,
          StepDecision.APPROVED,
          this.formData.reason,
          this.fullName,
          this.userRole,
          this.permission,
          this.maturityLevel(),
          ActionIcon.CHECK_REQUEST
        )
        .subscribe(({ isActiveRequest, canWithdraw }) => {
          this.isActiveRequest.set(isActiveRequest);
          this.canWithdraw.set(canWithdraw);
          this.newStepAdded.emit();
          if (this.permission === PermissionContext.MeasureApprovalRequestStep2) {
            this.maturityLevelChanged.emit(
              this.approvalRequestService.nextMaturityLevel(
                this.maturityLevel()
              )
            );
          }
          this.clearForm();
          this.timelineRequested.emit(true);
          this.isLoading.set(false);
        });
    }
  }

  private verifyRecentRequest(): boolean {
    return this.approvalRequestService.verifyRecentRequest(this.steps[0]);
  }

  private verifyWithdrawStep(): boolean {
    return this.approvalRequestService.verifyWithdrawStep(
      this.steps[0],
      this.permission
    );
  }

  private clearValidation(): void {
    if (this.permission == PermissionContext.MeasureChangeHartegrad)
      this.maturityLevelValidator?.instance.reset();
    this.reasonValidator?.instance.reset();
  }

  private validate(): boolean {
    var maturityLevelValidation = true;
    if (this.permission == PermissionContext.MeasureChangeHartegrad)
      maturityLevelValidation =
        this.maturityLevelValidator.instance.validate().isValid;
    return (
      this.reasonValidator.instance.validate().isValid &&
      maturityLevelValidation
    );
  }
}
